import React, { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import GlobalLayout from './GlobalLayout';
import { getUserInfos } from '../services/auth';
import { getStoredAllowedApps } from '../services/apps';

const normalizeText = (value) => String(value || '').trim();

const getFirstValue = (source, keys) => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && normalizeText(value)) {
            return value;
        }
    }

    return '';
};

const parseMaybeJson = (value, fallback) => {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const normalizeRoles = (roles) => {
    const parsedRoles = parseMaybeJson(roles, []);

    if (Array.isArray(parsedRoles)) {
        return parsedRoles
            .map((role) => {
                if (typeof role === 'object') {
                    return role?.label || role?.name || role?.code || role?.id || '';
                }

                return role;
            })
            .map((role) => normalizeText(role))
            .filter(Boolean);
    }

    if (typeof parsedRoles === 'string') {
        return parsedRoles.split(',').map((role) => normalizeText(role)).filter(Boolean);
    }

    if (parsedRoles && typeof parsedRoles === 'object') {
        return Object.values(parsedRoles).map((role) => normalizeText(role)).filter(Boolean);
    }

    return [];
};

const getUserFullName = (userInfos) => {
    const firstname = getFirstValue(userInfos, ['firstname', 'firstName', 'given_name']);
    const lastname = getFirstValue(userInfos, ['lastname', 'lastName', 'family_name']);
    const fullName = [firstname, lastname].filter(Boolean).join(' ').trim();

    return fullName || getFirstValue(userInfos, ['name', 'username', 'preferred_username', 'email', 'sub']) || 'Utilisateur';
};

const getUserInitials = (label) => {
    const parts = normalizeText(label).split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }

    return normalizeText(label).slice(0, 2).toUpperCase() || 'U';
};

const formatDate = (timestamp) => {
    if (!timestamp) return 'Non renseigné';

    const numericTimestamp = Number(timestamp);
    const date = new Date(numericTimestamp > 10000000000 ? numericTimestamp : numericTimestamp * 1000);

    if (Number.isNaN(date.getTime())) return 'Non renseigné';

    return new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const getClientLabel = (userInfos) => {
    const directClient = getFirstValue(userInfos, ['client_name', 'clientName', 'client']);
    if (typeof directClient === 'object') {
        return directClient?.client_name || directClient?.name || directClient?.label || '';
    }

    return directClient || getFirstValue(userInfos, ['client_id', 'clientId']);
};

const getSortedAllowedApps = () => [...getStoredAllowedApps()].sort((firstApp, secondApp) => {
    const firstRank = Number(firstApp?.rank);
    const secondRank = Number(secondApp?.rank);
    const rankDiff = (Number.isFinite(firstRank) ? firstRank : Number.MAX_SAFE_INTEGER)
        - (Number.isFinite(secondRank) ? secondRank : Number.MAX_SAFE_INTEGER);

    if (rankDiff !== 0) return rankDiff;

    return (firstApp?.label || firstApp?.code || '').localeCompare(
        secondApp?.label || secondApp?.code || '',
        'fr',
        { sensitivity: 'base' }
    );
});

const DetailRow = ({ label, value }) => (
    <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {value || 'Non renseigné'}
        </Typography>
    </Stack>
);

const SectionTitle = ({ icon, children }) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'var(--accent)', display: 'inline-flex' }}>
            {icon}
        </Box>
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, mb: 0 }}>
            {children}
        </Typography>
    </Stack>
);

const AccountDetails = () => {
    const userInfos = getUserInfos() || {};
    const roles = useMemo(() => normalizeRoles(userInfos.roles), [userInfos.roles]);
    const allowedApps = useMemo(() => getSortedAllowedApps(), []);
    const fullName = getUserFullName(userInfos);
    const username = getFirstValue(userInfos, ['username', 'preferred_username', 'email', 'sub']);
    const email = getFirstValue(userInfos, ['email']);
    const userId = getFirstValue(userInfos, ['id', 'user_id', 'userId', 'sub']);
    const clientLabel = getClientLabel(userInfos);
    const isSudoSession = Boolean(localStorage.getItem('sudoer_token'));

    return (
        <GlobalLayout>
            <Box sx={{ maxWidth: 1180, mx: 'auto', p: { xs: 1, md: 2 } }}>
                <Stack spacing={2.5}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            border: '1px solid var(--border-color)',
                            borderRadius: 1,
                            bgcolor: 'var(--card-bg)'
                        }}
                    >
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                            <Avatar
                                sx={{
                                    width: 64,
                                    height: 64,
                                    bgcolor: 'var(--accent)',
                                    color: '#fff',
                                    fontWeight: 700
                                }}
                            >
                                {getUserInitials(fullName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0, overflowWrap: 'anywhere' }}>
                                        {fullName}
                                    </Typography>
                                    {isSudoSession && <Chip size="small" color="warning" label="Sudo actif" />}
                                </Stack>
                                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, overflowWrap: 'anywhere' }}>
                                    {email || username || 'Compte utilisateur courant'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)' },
                            gap: 2
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid var(--border-color)',
                                borderRadius: 1,
                                bgcolor: 'var(--card-bg)'
                            }}
                        >
                            <Stack spacing={2}>
                                <SectionTitle icon={<AccountCircleOutlinedIcon fontSize="small" />}>
                                    Identité
                                </SectionTitle>
                                <Divider />
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                        gap: 2
                                    }}
                                >
                                    <DetailRow label="Identifiant" value={userId} />
                                    <DetailRow label="Nom d'utilisateur" value={username} />
                                    <DetailRow label="Email" value={email} />
                                    <DetailRow label="Client" value={clientLabel} />
                                </Box>
                            </Stack>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid var(--border-color)',
                                borderRadius: 1,
                                bgcolor: 'var(--card-bg)'
                            }}
                        >
                            <Stack spacing={2}>
                                <SectionTitle icon={<VpnKeyOutlinedIcon fontSize="small" />}>
                                    Session
                                </SectionTitle>
                                <Divider />
                                <DetailRow label="Jeton émis le" value={formatDate(userInfos.iat)} />
                                <DetailRow label="Expiration" value={formatDate(userInfos.exp)} />
                            </Stack>
                        </Paper>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 0.8fr) minmax(0, 1.2fr)' },
                            gap: 2
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid var(--border-color)',
                                borderRadius: 1,
                                bgcolor: 'var(--card-bg)'
                            }}
                        >
                            <Stack spacing={2}>
                                <SectionTitle icon={<ShieldOutlinedIcon fontSize="small" />}>
                                    Rôles
                                </SectionTitle>
                                <Divider />
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {roles.length > 0 ? roles.map((role) => (
                                        <Chip key={role} label={role} size="small" />
                                    )) : (
                                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                                            Aucun rôle renseigné.
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid var(--border-color)',
                                borderRadius: 1,
                                bgcolor: 'var(--card-bg)'
                            }}
                        >
                            <Stack spacing={2}>
                                <SectionTitle icon={<AppsOutlinedIcon fontSize="small" />}>
                                    Applications autorisées
                                </SectionTitle>
                                <Divider />
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {allowedApps.length > 0 ? allowedApps.map((app) => (
                                        <Chip
                                            key={app.id || app.code}
                                            label={app.label || app.name || app.code || `App #${app.id}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )) : (
                                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                                            Aucune application en cache.
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: '1px solid var(--border-color)',
                            borderRadius: 1,
                            bgcolor: 'var(--card-bg)'
                        }}
                    >
                        <Stack spacing={2}>
                            <SectionTitle icon={<BadgeOutlinedIcon fontSize="small" />}>
                                Claims techniques
                            </SectionTitle>
                            <Divider />
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid var(--border-color)',
                                    bgcolor: 'var(--bg-modal-header)',
                                    color: 'var(--text-color)',
                                    overflow: 'auto',
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                    maxHeight: 260
                                }}
                            >
                                {JSON.stringify(userInfos, null, 2)}
                            </Box>
                        </Stack>
                    </Paper>
                </Stack>
            </Box>
        </GlobalLayout>
    );
};

export default AccountDetails;
